/**
 * Primary Visit Note CTA for the patient sidebar.
 *
 * Shows `Continue Visit Note` when the patient has a latest open encounter,
 * otherwise `Create Visit Note`. Lives in the sidebar's top action area,
 * independent of the active tab — PRD.179.
 */
import { useMemo } from 'react';
import { NavIcon } from './shared';
import { usePowerSyncQuery } from '../adapters/powersync';
import {
  qEncountersByPatient,
  qPatientWorkspaceSettings,
  mapPatientWorkspaceSettingsToUI,
  type PatientEncounterRow,
  type PatientWorkspaceSettingsRow,
} from '@helios/data-access';

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function fmtTime(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function PatientVisitNoteCTA({
  patientId,
  onContinue,
  onCreate,
  compact = false,
  variant = 'full',
}: {
  patientId: string;
  onContinue: (encounterId: string) => void;
  onCreate: () => void;
  compact?: boolean;
  /** `full` = full-width button; `icon` = icon-only for collapsed rail. */
  variant?: 'full' | 'icon';
}) {
  const { data: encounterRows = [] } = usePowerSyncQuery<PatientEncounterRow>(
    qEncountersByPatient(patientId),
  );
  // Hide this CTA entirely in Treatment Record mode (PRD.182 §5.5) — the
  // shared quick toolbar replaces it as the primary entry surface.
  const { data: workspaceSettingsRows = [] } = usePowerSyncQuery<PatientWorkspaceSettingsRow>(
    qPatientWorkspaceSettings(),
  );
  const isTreatmentRecordMode =
    workspaceSettingsRows.length > 0 &&
    mapPatientWorkspaceSettingsToUI(workspaceSettingsRows[0]).workspaceMode === 'treatment_record';

  const latestOpen = useMemo(() => {
    const sorted = [...encounterRows].sort((a, b) => {
      const da = a.started_at ?? a.created_at ?? '';
      const db = b.started_at ?? b.created_at ?? '';
      return new Date(db).getTime() - new Date(da).getTime();
    });
    return sorted.find((e) => e.status === 'open');
  }, [encounterRows]);

  const isContinue = Boolean(latestOpen);
  const subtitle = latestOpen
    ? `${fmtDate(latestOpen.started_at)} · ${fmtTime(latestOpen.started_at)}`
    : null;

  const handleClick = () => {
    if (latestOpen) onContinue(latestOpen.id);
    else onCreate();
  };

  if (isTreatmentRecordMode) return null;

  if (variant === 'icon') {
    const label = isContinue ? 'Continue Visit Note' : 'Create Visit Note';
    return (
      <button
        onClick={handleClick}
        title={label}
        aria-label={label}
        style={{
          width: 44,
          height: 44,
          borderRadius: 'var(--radius-md)',
          border: 'none',
          background: 'var(--color-primary, #2563eb)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          padding: 0,
          flexShrink: 0,
          boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
        }}
      >
        <NavIcon name={isContinue ? 'edit' : 'plus'} />
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 8 : 10,
        width: '100%',
        padding: compact ? '6px var(--sp-3)' : 'var(--sp-3) var(--sp-4)',
        borderRadius: 'var(--radius-md)',
        border: 'none',
        background: 'var(--color-primary, #2563eb)',
        color: '#fff',
        cursor: 'pointer',
        textAlign: 'left',
        boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
        fontFamily: 'inherit',
        transition: 'padding 200ms ease, gap 200ms ease',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: compact ? 20 : 28,
          height: compact ? 20 : 28,
          borderRadius: 'var(--radius-sm)',
          background: 'rgba(255,255,255,0.16)',
          flexShrink: 0,
          transition: 'width 200ms ease, height 200ms ease',
        }}
      >
        <NavIcon name={isContinue ? 'edit' : 'plus'} />
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
        <span
          style={{
            fontSize: compact ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semi)',
            lineHeight: 1.2,
          }}
        >
          {isContinue ? 'Continue Visit Note' : 'Create Visit Note'}
        </span>
        {!compact && subtitle && (
          <span
            style={{
              fontSize: 10,
              opacity: 0.85,
              marginTop: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {subtitle}
          </span>
        )}
      </span>
    </button>
  );
}
